import { Component } from '@theme/component';

/**
 * Shares files through the customer's chosen application. No upload endpoint,
 * persistence, telemetry or form field contains the selected files or their names.
 * @extends {Component<{picker: HTMLElement, input: HTMLInputElement, list: HTMLUListElement, share: HTMLButtonElement, clear: HTMLButtonElement, status: HTMLElement, whatsapp: HTMLAnchorElement, email?: HTMLAnchorElement}>}
 */
class BastiafreshFileTransfer extends Component {
  requiredRefs = ['picker', 'input', 'list', 'share', 'clear', 'status', 'whatsapp'];
  #files = [];
  #sharing = false;
  #generation = 0;

  connectedCallback() {
    super.connectedCallback();
    // Unsupported browsers keep the working manual WhatsApp/e-mail links.
    this.refs.picker.hidden = !(window.isSecureContext && typeof navigator.share === 'function' && typeof navigator.canShare === 'function');
    this.#refresh();
    this.refs.status.textContent = '';
  }

  disconnectedCallback() {
    this.#generation += 1;
    this.#files = [];
    this.#sharing = false;
    this.refs.input.value = '';
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#generation += 1;
    this.#files = [];
    this.#sharing = false;
    this.refs.input.value = '';
    this.#refresh();
    this.refs.status.textContent = '';
  }

  /** @param {Event} event */
  selectFiles(event) {
    if (this.#sharing || event.target !== this.refs.input) return;
    const files = Array.from(this.refs.input.files || []);
    if (files.length > 5 || files.some((file) => file.size > 20 * 1024 * 1024)) {
      this.#files = [];
      this.refs.input.value = '';
      this.#refresh();
      this.#status(files.length > 5 ? 'countError' : 'sizeError');
      return;
    }
    this.#files = files;
    this.#refresh();
    if (!files.length) this.#status('cleared');
    else if (this.#canShare()) this.refs.status.textContent = (this.dataset.selected || '').replace('[count]', String(files.length));
    else this.#status('unavailable');
  }

  clearFiles() {
    if (this.#sharing) return;
    this.#files = [];
    this.refs.input.value = '';
    this.#refresh();
    this.#status('cleared');
  }

  async shareFiles() {
    if (this.#sharing || !this.#canShare()) return;
    const generation = this.#generation;
    this.#sharing = true;
    this.#refresh();
    try {
      // Called directly from a click, preserving the required user activation.
      await navigator.share({ files: this.#files, title: this.dataset.subject || '', text: this.#message() });
      if (this.isConnected && generation === this.#generation) this.#status('finish');
    } catch (error) {
      if (this.isConnected && generation === this.#generation) this.#status(error?.name === 'AbortError' ? 'cancelled' : 'error');
    } finally {
      if (this.isConnected && generation === this.#generation) {
        this.#sharing = false;
        this.#refresh();
      }
    }
  }

  prepareMessage() {
    const message = this.#message();
    const whatsapp = new URL(this.refs.whatsapp.href);
    whatsapp.searchParams.set('text', message);
    this.refs.whatsapp.href = whatsapp.href;
    if (this.refs.email) {
      const email = new URL(this.refs.email.href);
      email.search = new URLSearchParams({ subject: this.dataset.subject || '', body: message }).toString();
      this.refs.email.href = email.href;
    }
    // Links start a conversation; they never attach a local file automatically.
  }

  #message() {
    const message = [this.dataset.message || ''];
    const form = document.getElementById(this.dataset.formId || '');
    if (!(form instanceof HTMLFormElement)) return message.join('\n');
    const name = form.elements.namedItem('contact[name]');
    const service = form.elements.namedItem('contact[service]');
    if (name instanceof HTMLInputElement && name.value.trim()) message.push(`${this.dataset.nameLabel}: ${name.value.trim().slice(0, 200)}`);
    if (service instanceof HTMLSelectElement && service.value) message.push(`${this.dataset.serviceLabel}: ${service.selectedOptions[0]?.textContent.trim().slice(0, 200) || ''}`);
    return message.join('\n');
  }

  #canShare() {
    if (!this.#files.length || typeof navigator.canShare !== 'function') return false;
    try {
      return navigator.canShare({ files: this.#files });
    } catch {
      return false;
    }
  }

  #refresh() {
    this.refs.list.replaceChildren(...this.#files.map((file) => {
      const item = document.createElement('li');
      item.textContent = file.name;
      return item;
    }));
    this.refs.list.hidden = !this.#files.length;
    this.refs.share.hidden = !this.#canShare();
    this.refs.clear.hidden = !this.#files.length;
    this.refs.share.disabled = this.#sharing;
    this.refs.clear.disabled = this.#sharing;
    this.refs.input.disabled = this.#sharing;
  }

  #status(key) {
    this.refs.status.textContent = this.dataset[key] || '';
  }
}

if (!customElements.get('bastiafresh-file-transfer')) {
  customElements.define('bastiafresh-file-transfer', BastiafreshFileTransfer);
}
